import React, { useState, useEffect, useRef } from 'react';
import { inboxAPI } from '../api';
import { socket } from '../App';

function Inbox() {
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [campaignContext, setCampaignContext] = useState(null);
  const [isLidUnresolved, setIsLidUnresolved] = useState(false);
  const [loading, setLoading] = useState(true);
  const [replyMessage, setReplyMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [justSent, setJustSent] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    loadConversations();

    // Listen for new messages via WebSocket
    socket.on('new-message', (data) => {
      loadConversations();
      if (selectedConversation === data.phoneNumber) {
        loadMessages(data.phoneNumber);
      }
    });

    return () => {
      socket.off('new-message');
    };
  }, [selectedConversation]);

  const loadConversations = async () => {
    try {
      const response = await inboxAPI.getConversations();
      setConversations(response.data);
    } catch (error) {
      console.error('Error loading conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMessages = async (phoneNumber) => {
    try {
      const response = await inboxAPI.getConversation(phoneNumber);
      setMessages(response.data.messages || response.data);
      setCampaignContext(response.data.campaignContext || null);
      setIsLidUnresolved(response.data.isLidUnresolved || false);
      setSelectedConversation(phoneNumber);
      
      // Mark as read
      await inboxAPI.markRead(phoneNumber);
      loadConversations(); // Refresh to update unread count
    } catch (error) {
      console.error('Error loading messages:', error);
    }
  };

  const handleSendReply = async (e) => {
    e.preventDefault();
    if (!replyMessage.trim() || !selectedConversation) return;
    
    const messageToSend = replyMessage.trim();
    setSending(true);
    
    // Optimistic update - add message to UI immediately
    const tempMessage = {
      phone_number: selectedConversation,
      message_content: messageToSend,
      is_from_me: 1,
      timestamp: new Date().toISOString()
    };
    setMessages(prev => [...prev, tempMessage]);
    setReplyMessage('');
    
    try {
      await inboxAPI.sendReply(selectedConversation, messageToSend);
      // Reload messages to get the actual saved message with ID
      const response = await inboxAPI.getConversation(selectedConversation);
      setMessages(response.data.messages || response.data);
      setCampaignContext(response.data.campaignContext || null);
      setIsLidUnresolved(response.data.isLidUnresolved || false);
      // Refresh conversations to update last message
      loadConversations();
      
      // Show success feedback briefly
      setJustSent(true);
      setTimeout(() => setJustSent(false), 2000);
    } catch (error) {
      console.error('Error sending reply:', error);
      alert('Failed to send message: ' + (error.response?.data?.error || error.message));
      // Remove optimistic message on error
      setMessages(prev => prev.filter(msg => msg !== tempMessage));
      setReplyMessage(messageToSend); // Restore message
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return <div className="container"><div className="loading">Loading inbox...</div></div>;
  }

  return (
    <div className="container">
      <h1>Inbox</h1>

      <div className="inbox-container">
        <div className="conversations-list">
          <h3 style={{ padding: '15px', margin: 0, borderBottom: '1px solid #ddd' }}>
            Conversations
          </h3>
          {conversations.length === 0 ? (
            <div className="empty-state">
              <p>No messages yet</p>
            </div>
          ) : (
            conversations.map(conv => (
              <div
                key={conv.phone_number}
                className={`conversation-item ${selectedConversation === conv.phone_number ? 'active' : ''}`}
                onClick={() => loadMessages(conv.phone_number)}
              >
                <div className="conversation-header">
                  <strong>
                    {conv.contact_name || `Unknown (+${conv.phone_number})`}
                  </strong>
                  {conv.unread_count > 0 && (
                    <span className="badge-notification">{conv.unread_count}</span>
                  )}
                </div>
                {conv.contact_name && conv.contact_name !== conv.phone_number && (
                  <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>
                    +{conv.phone_number}
                  </div>
                )}
                <div className="conversation-preview">{conv.last_message?.substring(0, 50)}...</div>
                <div className="conversation-time">
                  {new Date(conv.last_message_time).toLocaleString()}
                </div>
              </div>
            ))
          )}
        </div>

        <div className="messages-panel">
          {selectedConversation ? (
            <>
              <div className="messages-header">
                <h3>
                  {(() => {
                    const conv = conversations.find(c => c.phone_number === selectedConversation);
                    const name = conv?.contact_name;
                    const phone = conv?.phone_number || selectedConversation;
                    
                    if (name && name !== phone) {
                      return `${name} (+${phone})`;
                    } else {
                      return `Unknown (+${phone})`;
                    }
                  })()}
                </h3>
              </div>
              <div className="messages-list">
                {campaignContext && (
                  <div className="campaign-context">
                    <div className="campaign-context-header">
                      <strong>📋 Campaign Template</strong>
                      {campaignContext.campaign_name && (
                        <span style={{ marginLeft: '10px', fontSize: '13px', color: '#fff' }}>
                          {campaignContext.campaign_name}
                        </span>
                      )}
                      <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.9)', marginTop: '4px' }}>
                        Sent {new Date(campaignContext.timestamp).toLocaleString()}
                        {campaignContext.matched_contact_name && (
                          <span style={{ marginLeft: '10px' }}>
                            • Matched by name: {campaignContext.matched_contact_name}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="campaign-context-message">
                      {campaignContext.campaign_message}
                      {campaignContext.media_url && (
                        <div style={{ marginTop: '8px', fontSize: '12px', color: 'rgba(255,255,255,0.8)' }}>
                          📎 Media: {campaignContext.media_url}
                        </div>
                      )}
                    </div>
                  </div>
                )}
                {!campaignContext && isLidUnresolved && (
                  <div className="lid-warning">
                    <strong>⚠️ WhatsApp LID Detected</strong>
                    <p style={{ fontSize: '13px', marginTop: '8px', lineHeight: '1.5' }}>
                      This contact is using a WhatsApp LID (privacy identifier) instead of their phone number.
                      We couldn't automatically match it to the campaign message we sent.
                      The conversation context may be limited.
                    </p>
                  </div>
                )}
                {messages.map((msg, index) => (
                  <div
                    key={index}
                    className={`message ${msg.is_from_me ? 'message-sent' : 'message-received'}`}
                  >
                    <div className="message-content">{msg.message_content}</div>
                    <div className="message-time">
                      {new Date(msg.timestamp).toLocaleTimeString()}
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>
              <form onSubmit={handleSendReply} className="message-input-form">
                <input
                  type="text"
                  className="message-input"
                  placeholder="Type a message..."
                  value={replyMessage}
                  onChange={(e) => setReplyMessage(e.target.value)}
                  disabled={sending}
                  autoFocus
                />
                <button 
                  type="submit" 
                  className="btn btn-primary"
                  disabled={sending || !replyMessage.trim()}
                  style={{ minWidth: '100px' }}
                >
                  {sending ? '📤 Sending...' : justSent ? '✓ Sent' : '📤 Send'}
                </button>
              </form>
            </>
          ) : (
            <div className="empty-state">
              <h3>Select a conversation</h3>
              <p>Choose a conversation from the left to view messages</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Inbox;
